import capitalize from 'lodash/capitalize';
import debounce from 'lodash/debounce';
import { track } from './analytics-tracker';

type AnalyticsAttributes = Record<string, unknown>;
type FilterPanelCategory = 'policies' | 'rates';

class Analytics {
  // AI Assistant Events
  aiAssistant = {
    /** @param {'lender-question'|'lender-search'} questionType */
    /** @param {String} region */
    /** @param {'Alternate'|'Bank'|'Credit Union'|'Monoline'} lenderType */
    /** @param {Array} lenders */
    askQuestion: (questionType: string, region: string, lenderType: string, lenders: string[]) =>
      trackAiAssistant('ask_question', {
        question_type: questionType,
        region,
        lender_type: lenderType,
        lenders,
      }),

    /** @param {Array} lenders */
    filterByLender: (lenders: string[]) => trackAiAssistant('filter_by_lender', { lenders }),

    /** @param {'Alternate'|'Bank'|'Credit Union'|'Monoline'} lenderType */
    lenderTypeSelect: (lenderType: string) => trackAiAssistant('lender_type_select', { lender_type: lenderType }),

    /** @param {'lender-question'|'lender-search'} questionType */
    questionTypeClick: (questionType: string) =>
      trackAiAssistant('question_type_click', { question_type: questionType }),

    /** @param {String} region */
    regionSelect: (region: string) => trackAiAssistant('region_select', { region }),
  };

  // AltLending Events
  altLending = {
    /**
     * @param {Number} lenderId
     * @param {String} loanOption
     */
    backToResults: (lenderId: number, loanOption: string) =>
      trackAltLending('back_to_results', {
        lender_id: lenderId,
        loan_option: loanOption,
      }),

    backToSearch: () => trackAltLending('back_to_search'),

    clearFilters: () => trackAltLending('clear_filters'),

    /**
     * Track when users click on the contact lender button.
     * @param {Number} lenderId
     * @param {String} loanOption
     */
    contactLenderClick: (lenderId: number, loanOption: string) =>
      trackAltLending('contact_lender_click', { lender_id: lenderId, loan_option: loanOption }),

    /** @param {Object} filter */
    filterChange: (filter: AnalyticsAttributes) => trackAltLending('filter_change', filter),

    /** @param {Number} lenderId */
    /** @param {String} loanOption */
    /** @param {Number} solutionId */
    finmoCTAClick: (lenderId: number, loanOption: string, solutionId: number) =>
      trackAltLending('finmo_cta_click', {
        lender_id: lenderId,
        loan_option: loanOption,
        solution_id: solutionId,
      }),

    /**
     * Track changes to solution card layout.
     * @param {'card'|'table'} layout
     */
    layoutView: (layout: 'card' | 'table') => trackAltLending(`${layout}_layout_change`),

    /**
     * Track when users click on a lender card.
     * @param {Number} lenderId
     */
    lenderCardClick: (lenderId: number) => trackAltLending('lender_card_click', { lender_id: lenderId }),

    /**
     * @param {String} loanType
     * @param {String} province
     */
    search: (loanType: string, province: string) =>
      trackAltLending('search', {
        loan_type: capitalize(loanType),
        province,
      }),

    /** @param {Number} lenderId */
    /** @param {String} loanOption */
    /** @param {Number} solutionId */
    solutionCardClick: (lenderId: number, loanOption: string, solutionId: number) =>
      trackAltLending('solution_card_click', {
        lender_id: lenderId,
        loan_option: loanOption,
        solution_id: solutionId,
      }),

    /**
     * Track when users click on a lender name or logo on a solution card.
     * @param {Number} lenderId
     */
    solutionCardLenderClick: (lenderId: number) =>
      trackAltLending('solution_card_lender_click', { lender_id: lenderId }),

    /**
     * Track when alternative lending tooltips are opened.
     * @param {String} filter
     */
    tooltipDisplay: (filter: string) => trackAltLending('tooltip_display', { filter }),
  };

  // Billing Events
  billing = {
    /**
     * Track when users accept a retention offer.
     * @param {String} plan
     * @param {String} discount
     */
    acceptRetentionOffer: (plan: string, discount: string) =>
      trackBilling('accept_retention_offer', { plan, discount }),

    /**
     * Track when users delete their payment method.
     * @param {String} page
     */
    deletePayment: () => trackBilling('delete_payment_method'),

    /**
     * Track when users downgrades a subscription.
     * @param {String} plan
     */
    downgrade: (plan: string) => trackBilling('cancel_subscription', { plan }),

    /**
     * Track when users resume a downgraded subscription.
     * @param {String} plan
     */
    resume: (plan: string) => trackBilling('resume_subscription', { plan }),

    /**
     * Track when users set their payment method.
     * @param {String} page
     */
    setPayment: () => trackBilling('set_payment_method'),

    /**
     * Track when users subscribe to a plan.
     * @param {String} plan
     */
    subscribe: (plan: string) => trackBilling('subscribe', { plan }),

    /**
     * Track when users switch their plan between yearly and monthly.
     * @param {String} plan
     */
    switch: (plan: string) => trackBilling('switch_subscription', { plan }),

    /**
     * Track when users update their payment method.
     * @param {String} page
     */
    updatePayment: () => trackBilling('update_payment_method'),
  };

  // Compare Events
  compare = {
    backButtonClick: () => trackCompare('back_button_click'),

    clearAll: () => trackCompareMenu('clear_all'),

    /** @param {Boolean} isOpeningSidebar */
    menuIconClick: (isOpeningSidebar: boolean) =>
      trackCompareMenu('icon_click', {
        state: isOpeningSidebar ? 'open' : 'close',
      }),

    /** @param {String} policy */
    policyCollapse: (policy: string) => trackCompare('policy_collapse', { policy }),

    /** @param {String} policy */
    policyExpand: (policy: string) => trackCompare('policy_expand', { policy }),

    /** @param {Boolean} isOpen */
    reportModal: (isOpen: boolean) => trackCompare('create_report_click', { state: isOpen ? 'open' : 'close' }),

    /** @param {String} fields */
    reportSubmit: (fields: string) => trackCompare('create_report_submit', { fields }),

    /** @param {Number} productCount */
    start: (productCount: number) => trackCompareMenu('compare_start', { count: productCount }),
  };

  // LenderDetails Events
  lenderDetails = {
    /**
     * Track when users submit the alternative lender contact form.
     * @param {Number} lenderId
     * @param {'Traditional'|'Alternative'} type
     */
    contactFormSubmit: (lenderId: number, type: string) =>
      trackLenderDetails('contact_form_submit', { lender_id: lenderId, type }),

    /**
     * Track when users click on the lender details tab controls.
     * @param {Number} lenderId
     * @param {'Contact lender'|'Documents'|'Guidelines'|'Loan solutions'|'Rates'} tab
     * @param {'Traditional'|'Alternative'} type
     */
    tabClick: (lenderId: number, tab: string, type: string) => () =>
      trackLenderDetails('tab_click', { lender_id: lenderId, tab, type }),

    userNotes: {
      /**
       * Track when a personal lender note is created.
       * @param {String} lenderName
       * @param {'Traditional'|'Alternative'} type
       * @param {'Lender details'|'Product details'} page
       */
      create: (lenderName: string, type: string, page: string) =>
        trackLenderDetails('user_notes_create', { lender_name: lenderName, type, page }),

      /**
       * Track when a personal lender note is deleted.
       * @param {String} lenderName
       * @param {'Traditional'|'Alternative'} type
       * @param {'Lender details'|'Product details'} page
       */
      delete: (lenderName: string, type: string, page: string) =>
        trackLenderDetails('user_notes_delete', { lender_name: lenderName, type, page }),

      /**
       * Track when a personal lender note is edited.
       * @param {String} lenderName
       * @param {'Traditional'|'Alternative'} type
       * @param {'Lender details'|'Product details'} page
       */
      edit: (lenderName: string, type: string, page: string) =>
        trackLenderDetails('user_notes_edit', { lender_name: lenderName, type, page }),
    },

    /**
     * Track when users click on a lender's website link.
     * @param {Number} lenderId
     * @param {'Traditional'|'Alternative'} type
     */
    websiteClick: (lenderId: number, type: string) => () =>
      trackLenderDetails('website_click', { lender_id: lenderId, type }),
  };

  // Lenders Events
  lenders = {
    /**
     * Track when users click on a lender card on the lender page.
     * @param {Number} lenderId
     * @param {'Traditional'|'Alternative'} type
     */
    cardClick: (lenderId: number, type: string) => () =>
      trackLenders('lender_card_click', { lender_id: lenderId, type }),

    /**
     * Track when users click on the lenders tabs.
     * @param {'Traditional'|'Alternative'} tab
     */
    tabClick: (tab: string) => () => trackLenders('tab_click', { tab }),

    // Preferred Lender Events
    preferred: {
      /**
       * Track when a user marks a lender as preferred.
       * @param {Number} lenderId
       * @param {'Traditional'|'Alternative'} type
       */
      added: (lenderId: number, type: string) => trackLenders('preferred_added', { lender_id: lenderId, type }),

      /**
       * Track when a user unmarks a lender as preferred.
       * @param {Number} lenderId
       * @param {'Traditional'|'Alternative'} type
       */
      removed: (lenderId: number, type: string) => trackLenders('preferred_removed', { lender_id: lenderId, type }),

      /**
       * Track when a user selects the "Preferred" sort option.
       * @param {String} page
       */
      sortSelected: (page: string) => trackLenders('preferred_sort_selected', { page }),
    },
  };

  // Navigation Events
  navigation = {
    onDesktop: trackNavigation('desktop'),
    onMobile: trackNavigation('mobile'),
  };

  // Policy Events
  policy = {
    /**
     * Track when users click on a policy card link.
     * @param {Number} lenderId
     * @param {'details'|'view rates'} target
     */
    cardClick: (lenderId: number, target: string) => () =>
      trackPolicy('policy_card_click', { lender_id: lenderId, target }),

    /**
     * Tracks when users filter policies by region.
     * @param {String} region
     */
    filterByRegion: (region: string) => trackPolicy('filter_by_region', { region }),
  };

  // ProductSearch Events
  productSearch = {
    filter: trackFilter,

    /**
     * Debounced version of trackFilter for use with text fields.
     * @return {Function}
     */
    filterDebounced: debounce(trackFilter, 400),

    /**
     * Tracks when filter panel is closed.
     * @param {'rates'|'policies'} category
     */
    filterPanelClose: (category: FilterPanelCategory = 'rates') =>
      track(category === 'rates' ? 'product_search' : 'policy')('filter_panel_close'),

    /**
     * Tracks when filter panel is opened.
     * @param {'rates'|'policies'} category
     */
    filterPanelOpen: (category: FilterPanelCategory = 'rates') =>
      track(category === 'rates' ? 'product_search' : 'policy')('filter_panel_open'),

    /**
     * Tracks when filter panel 'Show Results' button is clicked.
     * @param {'rates'|'policies'} category
     */
    filterPanelShowResultsClick: (category: FilterPanelCategory = 'rates') =>
      track(category === 'rates' ? 'product_search' : 'policy')('filter_panel_show_results'),

    /**
     * Track changes to lender and product card layout.
     * @param {'card'|'table'} layout
     */
    layoutView: (layout: 'card' | 'table') => trackProductSearch(`${layout}_layout_change`),

    /**
     * Tracks clicks on the lender cards in the products view.
     * @param {Number} lenderId
     */
    lenderCardClick: (lenderId: number) => trackProductSearch('lender_card_click', { lender_id: lenderId }),

    /**
     * Track lender click from Rate Card
     * @param {Number} lenderId
     */
    rateCardLenderClick: (lenderId: number) => trackProductSearch('rate_card_lender_click', { lender_id: lenderId }),

    /**
     * Track rate details click from Rate Card
     * @param {Number} lenderId
     */
    rateCardRateClick: (lenderId: number) => trackProductSearch('rate_card_rate_click', { lender_id: lenderId }),

    rateCompareLimitReached: () => trackProductSearch('compare_limit_reached'),

    /**
     * Track rate card comparison selection toggle with resulting status.
     * @param {Boolean} isSelecting
     * @param {Number} lenderId
     */
    rateCompareSelect: (isSelecting: boolean, lenderId: number) =>
      isSelecting
        ? trackProductSearch('compare_select', { lender_id: lenderId })
        : trackProductSearch('compare_deselect'),

    rateCompareSidebarUnselect: () => trackProductSearch('compare_sidebar_deselect'),

    /**
     * Track saved search operations.
     */
    savedFilters: {
      close: () => trackProductSearch('saved_filter_close'),
      create: () => trackProductSearch('saved_filter_create'),
      delete: () => trackProductSearch('saved_filter_delete'),

      /**
       * Track when the saved filters pane is opened.
       * @param {'aside'|'topbar'} location
       */
      open: (location: 'aside' | 'topbar') => trackProductSearch('saved_filter_open', { location }),
    },

    /**
     * Track changes to product sorting in lender view.
     * @param {'featured'|'lender'|'preferred'|'rate'} order
     */
    sort: (order: 'featured' | 'lender' | 'preferred' | 'rate') => {
      trackProductSearch(`${order}_sort`);
    },

    /**
     * Track when product tooltips are opened.
     * @param {String} filter
     */
    tooltipDisplay: (filter: string) => {
      trackProductSearch('tooltip_display', { filter });
    },

    /**
     * Track changes to product view-by options.
     * @param {String} path The path associated with the view change
     */
    view: (path: string) => {
      trackProductSearch(path.endsWith('lenders') ? 'lender_view_change' : 'rate_view_change');
    },
  };

  // Promotions Events
  promotions = {
    /**
     * Track clicks on promotion banner.
     * @param {String} lenderName
     * @param {String} page
     */
    adBannerClick: (lenderName: string, page: string) =>
      trackPromotions('ad_banner_click', { lender_name: lenderName, page }),
  };

  // Scenarios Events
  scenarios = {
    /**
     * Track when a borrower is added to a comparison.
     * @param {Number} borrowerCount
     */
    addBorrower: (borrowerCount: number) => trackScenarios('add_borrower', { borrower_count: borrowerCount }),

    /**
     * Track when a custom product is added to a comparison.
     * @param {'Product Selection'|'Scenario Builder'} page
     */
    addCustomProduct: (page: string) => trackScenarios('add_custom_product', { page }),

    /**
     * Track when a product is added to a comparison.
     * @param {String} lenderName
     * @param {String} productName
     */
    addProduct: (lenderName: string, productName: string) =>
      trackScenarios('add_product', { lender_name: lenderName, product_name: productName }),

    /**
     * Track when a scenario is added to a product.
     * @param {String} lenderName
     * @param {String} productName
     */
    addScenario: (lenderName: string, productName: string) =>
      trackScenarios('add_scenario', { lender_name: lenderName, product_name: productName }),

    /**
     * Track when a scenario is set as the base scenario for a comparison.
     * @param {String} lenderName
     * @param {String} productName
     */
    changeBase: (lenderName: string, productName: string) =>
      trackScenarios('change_base', { lender_name: lenderName, product_name: productName }),

    /**
     * Track clicks on the public comparison link on the management dashboard.
     */
    clickPublicLink: () => trackScenarios('click_public_link'),

    /**
     * Track when a comparison is started.
     */
    comparisonStart: () => trackScenarios('comparison_start'),

    /**
     * Track when a comparison's public link is copied to the clipboard.
     * @param {'Comparisons Management'|'Scenario Preview'} page
     */
    copyPublicLink: (page: string) => trackScenarios('copy_public_link', { page }),

    /**
     * Track when a comparison is first published and a public link is generated.
     */
    createAndPublishLink: () => trackScenarios('create_and_publish_link'),

    /**
     * Track when a new comparison is created.
     */
    createComparison: () => trackScenarios('create_comparison'),

    /**
     * Track when a borrower is removed from a comparison.
     * @param {Number} borrowerCount
     */
    deleteBorrower: (borrowerCount: number) => trackScenarios('delete_borrower', { borrower_count: borrowerCount }),

    /**
     * Track when a comparison is deleted.
     */
    deleteComparison: () => trackScenarios('delete_comparison'),

    /**
     * Track when a product is removed from a comparison.
     * @param {String} lenderName
     * @param {String} productName
     */
    deleteProduct: (lenderName: string, productName: string) =>
      trackScenarios('delete_product', { lender_name: lenderName, product_name: productName }),

    /**
     * Track when a scenario is removed from a product.
     * @param {String} lenderName
     * @param {String} productName
     */
    deleteScenario: (lenderName: string, productName: string) =>
      trackScenarios('delete_scenario', { lender_name: lenderName, product_name: productName }),

    /**
     * Track when a comparison is duplicated.
     */
    duplicateComparison: () => trackScenarios('duplicate_comparison'),

    /**
     * Track when a comparison is loaded.
     */
    loadComparison: () => trackScenarios('load_comparison'),

    /**
     * Track when a comparison is previewed and shared.
     */
    previewAndShareComparison: () => trackScenarios('preview_and_share_comparison'),

    /**
     * Track when the comparison product limit is reached.
     */
    productLimitReached: () => trackScenarios('product_limit_reached'),

    /**
     * Track when a comparison is published.
     * @param {'Comparisons Management'|'Scenario Preview'} page
     */
    publishComparison: (page: string) => trackScenarios('publish_comparison', { page }),

    /**
     * Tracks when all products are removed from a comparison.
     */
    removeAllProducts: () => trackScenarios('remove_all_products'),

    /**
     * Track when user attempts to remove the product containing the base scenario.
     * @param {String} lenderName
     * @param {String} productName
     */
    removeBaseScenarioWarning: (lenderName: string, productName: string) =>
      trackScenarios('remove_base_scenario_warning', { lender_name: lenderName, product_name: productName }),

    /**
     * Track when a product is removed from a comparison.
     * @param {String} lenderName
     * @param {String} productName
     */
    removeProduct: (lenderName: string, productName: string) =>
      trackScenarios('remove_product', { lender_name: lenderName, product_name: productName }),

    /**
     * Track when a borrower summary is saved on a comparison.
     */
    saveBorrowerSummary: () => trackScenarios('save_borrower_summary'),

    /**
     * Track when a comparison is saved.
     */
    saveComparison: () => trackScenarios('save_comparison'),

    /**
     * Track when a comparison is unpublished.
     * @param {'Comparisons Management'|'Scenario Preview'} page
     */
    unpublishComparison: (page: string) => trackScenarios('unpublish_comparison', { page }),
  };

  // Team Management Events
  teamManagement = {
    announcements: {
      create: (brokerageName: string) => trackTeamManagement('announcements_create', { brokerage: brokerageName }),

      delete: (brokerageName: string) => trackTeamManagement('announcements_delete', { brokerage: brokerageName }),

      edit: (brokerageName: string) => trackTeamManagement('announcements_edit', { brokerage: brokerageName }),
    },

    documents: {
      delete: (brokerageName: string) => trackTeamManagement('documents_delete', { brokerage: brokerageName }),

      download: (brokerageName: string) => trackTeamManagement('documents_download', { brokerage: brokerageName }),

      edit: (brokerageName: string) => trackTeamManagement('documents_edit', { brokerage: brokerageName }),

      upload: (brokerageName: string) => trackTeamManagement('documents_upload', { brokerage: brokerageName }),
    },

    lenders: {
      contacts: (brokerageName: string, lenderName: string) =>
        trackTeamManagement('lenders_contacts', { brokerage: brokerageName, lender: lenderName }),

      exclusiveRates: {
        create: (brokerageName: string, lenderName: string) =>
          trackTeamManagement('lenders_exclusive_rates_create', {
            brokerage: brokerageName,
            lender: lenderName,
          }),

        delete: (brokerageName: string, lenderName: string) =>
          trackTeamManagement('lenders_exclusive_rates_delete', {
            brokerage: brokerageName,
            lender: lenderName,
          }),

        duplicate: (brokerageName: string, lenderName: string) =>
          trackTeamManagement('lenders_exclusive_rates_duplicate', {
            brokerage: brokerageName,
            lender: lenderName,
          }),

        edit: (brokerageName: string, lenderName: string) =>
          trackTeamManagement('lenders_exclusive_rates_edit', {
            brokerage: brokerageName,
            lender: lenderName,
          }),

        publishLater: (brokerageName: string, lenderName: string) =>
          trackTeamManagement('lenders_exclusive_rates_publish_later', {
            brokerage: brokerageName,
            lender: lenderName,
          }),

        publishNow: (brokerageName: string, lenderName: string) =>
          trackTeamManagement('lenders_exclusive_rates_publish_now', {
            brokerage: brokerageName,
            lender: lenderName,
          }),

        saveDraft: (brokerageName: string, lenderName: string) =>
          trackTeamManagement('lenders_exclusive_rates_save_draft', {
            brokerage: brokerageName,
            lender: lenderName,
          }),

        unpublish: (brokerageName: string, lenderName: string) =>
          trackTeamManagement('lenders_exclusive_rates_unpublish', {
            brokerage: brokerageName,
            lender: lenderName,
          }),

        view: (brokerageName: string, lenderName: string) =>
          trackTeamManagement('lenders_exclusive_rates_view', {
            brokerage: brokerageName,
            lender: lenderName,
          }),
      },

      notes: {
        create: (brokerageName: string, lenderName: string) =>
          trackTeamManagement('lenders_notes_create', {
            brokerage: brokerageName,
            lender: lenderName,
          }),

        delete: (brokerageName: string, lenderName: string) =>
          trackTeamManagement('lenders_notes_delete', {
            brokerage: brokerageName,
            lender: lenderName,
          }),

        edit: (brokerageName: string, lenderName: string) =>
          trackTeamManagement('lenders_notes_edit', {
            brokerage: brokerageName,
            lender: lenderName,
          }),

        publish: (brokerageName: string, lenderName: string) =>
          trackTeamManagement('lenders_notes_publish', {
            brokerage: brokerageName,
            lender: lenderName,
          }),

        unpublish: (brokerageName: string, lenderName: string) =>
          trackTeamManagement('lenders_notes_unpublish', {
            brokerage: brokerageName,
            lender: lenderName,
          }),
      },

      statuses: (brokerageName: string, lenderName: string) =>
        trackTeamManagement('lenders_statuses', { brokerage: brokerageName, lender: lenderName }),

      tags: {
        create: (brokerageName: string, lenderName: string) =>
          trackTeamManagement('lenders_tags_create', {
            brokerage: brokerageName,
            lender: lenderName,
          }),

        delete: (brokerageName: string, lenderName: string) =>
          trackTeamManagement('lenders_tags_delete', {
            brokerage: brokerageName,
            lender: lenderName,
          }),

        edit: (brokerageName: string, lenderName: string) =>
          trackTeamManagement('lenders_tags_edit', {
            brokerage: brokerageName,
            lender: lenderName,
          }),

        publish: (brokerageName: string, lenderName: string) =>
          trackTeamManagement('lenders_tags_publish', {
            brokerage: brokerageName,
            lender: lenderName,
          }),

        unpublish: (brokerageName: string, lenderName: string) =>
          trackTeamManagement('lenders_tags_unpublish', {
            brokerage: brokerageName,
            lender: lenderName,
          }),
      },
    },

    members: {
      invitationRevoke: (brokerageName: string) =>
        trackTeamManagement('members_invitation_revoke', { brokerage: brokerageName }),

      invitationSend: (brokerageName: string) =>
        trackTeamManagement('members_invitation_send', { brokerage: brokerageName }),

      invitationSendReminder: (brokerageName: string) =>
        trackTeamManagement('members_invitation_send_reminder', { brokerage: brokerageName }),

      joinRequestAccept: (brokerageName: string) =>
        trackTeamManagement('members_join_request_accept', { brokerage: brokerageName }),

      joinRequestDecline: (brokerageName: string) =>
        trackTeamManagement('members_join_request_decline', { brokerage: brokerageName }),

      removeMember: (brokerageName: string) =>
        trackTeamManagement('members_remove_member', { brokerage: brokerageName }),

      setToAdmin: (brokerageName: string) => trackTeamManagement('members_set_to_admin', { brokerage: brokerageName }),

      setToMember: (brokerageName: string) =>
        trackTeamManagement('members_set_to_member', { brokerage: brokerageName }),
    },
  };

  // Teams Events
  teams = {
    downloadDocument: (brokerageName: string) => trackTeams('download_document', { brokerage: brokerageName }),

    trainingSignupClick: (brokerageName?: string) => trackTeams('training_signup_click', { brokerage: brokerageName }),
  };

  // Team Status Events
  teamStatus = {
    invitationAccept: (brokerageName: string) => trackTeamStatus('invitation_accept', { brokerage: brokerageName }),

    invitationDecline: (brokerageName: string) => trackTeamStatus('invitation_decline', { brokerage: brokerageName }),

    joinRequestCancel: (brokerageName: string) => trackTeamStatus('join_request_cancel', { brokerage: brokerageName }),

    joinRequestSend: (brokerageName: string) => trackTeamStatus('join_request_send', { brokerage: brokerageName }),

    leaveTeam: (brokerageName: string) => trackTeamStatus('leave_team', { brokerage: brokerageName }),

    switchTeam: (brokerageName: string) => trackTeamStatus('switch_team', { brokerage: brokerageName }),

    trainingSignupClick: () => trackTeamStatus('training_signup_click'),
  };

  // Upgrade Events
  upgrade = {
    /**
     * Track clicks on the upgrade basic user banner close button.
     * @param {String} bannerType the type of upgrade banner
     */
    bannerHide: (bannerType: string) => trackUpgrade('banner_hide', { banner_type: bannerType }),

    /**
     * Track clicks on the upgrade basic user banner link.
     * @param {String} bannerType the type of upgrade banner
     * @param {String} page the destination page of the link
     */
    bannerLinkClick: (bannerType: string, page: string) => () =>
      trackUpgrade('banner_link_click', { banner_type: bannerType, page }),

    /**
     * Tracks clicks on the Chat With Us top bar button.
     */
    chatWithUsButtonClick: () => trackUpgrade('chat_with_us_button_click'),

    /**
     * Track clicks on upgrade badges and links.
     * @param {String} target The target component associated with the badge/link
     */
    click: (target: string) => () => trackUpgrade('click', { target }),
  };

  // Your Account Events
  yourAccount = {
    changePassword: () => trackYourAccount('change_password'),

    updateProfileDetails: () => trackYourAccount('update_profile_details'),

    updateProfilePhoto: () => trackYourAccount('update_profile_photo'),
  };
}

/* Wrapper functions for setting area/event categories */
const trackAiAssistant = track('ai_assistant');
const trackAltLending = track('alt_lending');
const trackBilling = track('billing');
const trackCompare = track('compare');
const trackCompareMenu = track('compare_menu');
const trackLenderDetails = track('lender_details');
const trackLenders = track('lenders');
const trackPolicy = track('policy');
const trackProductSearch = track('product_search');
const trackPromotions = track('promotions');
const trackScenarios = track('scenarios');
const trackTeamManagement = track('tm'); // Shortened to avoid exceeding the 40-character event name limit
const trackTeams = track('teams');
const trackTeamStatus = track('team_status');
const trackUpgrade = track('upgrade');
const trackYourAccount = track('your_account');

/**
 * Track changes to rate filters.
 * @param {Object} filter
 */
const trackFilter = (filter: AnalyticsAttributes) => trackProductSearch('filter_change', filter);

/**
 * @param {'desktop'|'mobile'} device
 * @return {(page: string) => () => void}
 */
const trackNavigation = (device: 'desktop' | 'mobile') => (page: string) => () =>
  track('navigation')(`${device}_link_clicked`, { page });

export default new Analytics();
